import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import type { Suggestion } from "@interfaces";
import { useNavigate } from "react-router-dom";

const SuggestionsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const SuggestionRow = styled("button")(({ theme }) => ({
  border: "none",
  background: "white",
  padding: "12px 16px",
  width: "100%",
  cursor: "pointer",
  display: "flex",
  justifyContent: "flex-start",
  gap: "16px",
  alignItems: "center",
  borderBottom: "1px solid #eaeaea",
  "&:hover": {
    background: theme.palette.background.disabled,
  },
}));

const CenteredSuggestionRow = styled(SuggestionRow)({
  justifyContent: "center",
  gap: "8px",
});

const SuggestionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  textAlign: "left",
  color: theme.palette.text.secondary,
}));

const MainSuggestionTitle = styled(SuggestionTitle)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
}));

const SecondarySuggestionTitle = styled(SuggestionTitle)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  fontSize: "24px",
  width: "24px",
  height: "24px",
  color: theme.palette.icon.secondary,
}));

const MainIconContainer = styled(IconContainer)(({ theme }) => ({
  color: theme.palette.icon.primary,
}));

interface Props {
  className?: string;
  searchText: string;
  preliminarySearchResults: Suggestion[];
  onItemClick: (item: Suggestion) => void;
  showMostSearchedQueries: boolean;
  mostSearchedQueries: Suggestion[];
  addManuallyItemText?: string;
  locationState?: Record<string, unknown>;
}

export const PreliminarySearchResults = ({
  className,
  searchText,
  preliminarySearchResults,
  onItemClick,
  showMostSearchedQueries,
  mostSearchedQueries,
  addManuallyItemText,
  locationState,
}: Props) => {
  const navigate = useNavigate();

  const suggestionsInitialState = useMemo(() => {
    return {
      preliminarySearchResults: preliminarySearchResults.slice(0, 8),
      showMoreResultsButton: preliminarySearchResults.length > 8,
    };
  }, [preliminarySearchResults]);

  const [preliminarySearchResultsToShow, setPreliminarySearchResultsToShow] = useState<
    Suggestion[]
  >(suggestionsInitialState.preliminarySearchResults);

  const [showMoreResultsButton, setShowMoreResultsButton] = useState(
    suggestionsInitialState.showMoreResultsButton,
  );

  const handleAddManually = useCallback(() => {
    navigate("../añadir-manual", {
      state: {
        searchedQuery: searchText,
        ...locationState,
      },
    });
  }, [locationState, navigate, searchText]);

  const handleShowMoreResults = useCallback(() => {
    setPreliminarySearchResultsToShow(preliminarySearchResults);
    setShowMoreResultsButton(false);
  }, [preliminarySearchResults]);

  useEffect(() => {
    function limitSuggestionsOnSearchTextChange() {
      setPreliminarySearchResultsToShow(suggestionsInitialState.preliminarySearchResults);
      setShowMoreResultsButton(suggestionsInitialState.showMoreResultsButton);
    }

    limitSuggestionsOnSearchTextChange();
  }, [searchText, suggestionsInitialState]);

  if (showMostSearchedQueries && mostSearchedQueries) {
    return (
      <PreliminarySearchResults.MostSearchedQueries
        className={className}
        onItemClick={onItemClick}
        mostSearchedQueries={mostSearchedQueries}
        addManuallyItemText={addManuallyItemText}
        handleAddManually={handleAddManually}
      />
    );
  }

  return (
    <SuggestionsContainer className={className}>
      {preliminarySearchResultsToShow.map((item, i) => (
        <SuggestionRow key={`preliminary-${i}`} onClick={() => onItemClick(item)}>
          <IconContainer>
            <SearchIcon />
          </IconContainer>
          <SuggestionTitle>{item.text}</SuggestionTitle>
        </SuggestionRow>
      ))}
      {showMoreResultsButton && (
        <CenteredSuggestionRow onClick={handleShowMoreResults}>
          <SecondarySuggestionTitle>Mostrar más resultados</SecondarySuggestionTitle>
          <IconContainer>
            <KeyboardArrowDownIcon />
          </IconContainer>
        </CenteredSuggestionRow>
      )}
      {addManuallyItemText && (
        <CenteredSuggestionRow onClick={handleAddManually}>
          <MainSuggestionTitle>{addManuallyItemText}</MainSuggestionTitle>
          <MainIconContainer>
            <AddIcon />
          </MainIconContainer>
        </CenteredSuggestionRow>
      )}
    </SuggestionsContainer>
  );
};

PreliminarySearchResults.MostSearchedQueries = function MostSearchedQueries({
  className,
  onItemClick,
  mostSearchedQueries,
  addManuallyItemText,
  handleAddManually,
}: {
  className?: string;
  onItemClick: (item: Suggestion) => void;
  mostSearchedQueries: Suggestion[];
  addManuallyItemText?: string;
  handleAddManually: () => void;
}) {
  return (
    <SuggestionsContainer className={className}>
      {mostSearchedQueries.map((item: Suggestion, i: number) => (
        <SuggestionRow key={`preliminary-${i}`} onClick={() => onItemClick(item)}>
          <IconContainer>
            <SearchIcon />
          </IconContainer>
          <SuggestionTitle>{item.text}</SuggestionTitle>
        </SuggestionRow>
      ))}
      {addManuallyItemText && (
        <CenteredSuggestionRow onClick={handleAddManually}>
          <MainSuggestionTitle>{addManuallyItemText}</MainSuggestionTitle>
          <MainIconContainer>
            <AddIcon />
          </MainIconContainer>
        </CenteredSuggestionRow>
      )}
    </SuggestionsContainer>
  );
};
